import { PageHeader } from '@/shared/components/ui/PageHeader';
import { ScrollText } from 'lucide-react';
import { useState, useEffect } from 'react';
import { AccountingSubNav } from '../components/AccountingSubNav';
import { Plus, RefreshCw, CheckCircle2, AlertCircle, Send, CheckSquare, ArrowUpRight, RotateCcw, Layers, FileEdit, Bookmark, XCircle, Undo2, Trash2 } from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';
import { Badge } from '@/shared/components/ui/Badge';
import { api } from '@/shared/api/axios';
import { cn, localToday } from '@/shared/utils/utils';

interface JournalLineInput {
  accountId: string;
  debit: string;
  credit: string;
  description: string;
}

/** Voucher lifecycle states, each on the accessible `-text` pair so posted vs draft is unmissable. */
function StatusPill({ status }: { status: string }) {
  const tone = status === 'posted' ? 'success'
    : status === 'approved' ? 'info'
      : status === 'submitted' ? 'warning'
        : status === 'rejected' ? 'danger'
          : 'neutral';
  return <Badge tone={tone}>{status}</Badge>;
}

export function JournalListPage() {
  const [journals, setJournals] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // Kept distinct from "no journals": a refused or failed read must never look
  // like a nil register.
  const [loadError, setLoadError] = useState<{ denied: boolean; message: string } | null>(null);
  const [statusTab, setStatusTab] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // New Journal Form state
  const [journalDate, setJournalDate] = useState(localToday());
  const [narration, setNarration] = useState('');
  const [reference, setReference] = useState('');
  const [lineItems, setLineItems] = useState<JournalLineInput[]>([
    { accountId: '', debit: '', credit: '', description: '' },
    { accountId: '', debit: '', credit: '', description: '' },
  ]);

  // Fetch accounts list for line item account selector
  useEffect(() => {
    api.get('/api/accounting/accounts')
      .then((res) => {
        const data = res.data;
        if (data.ok || data.success) {
          const list = Array.isArray(data.data) ? data.data : data.data?.items || [];
          setAccounts(list.filter((a: any) => !a.isGroupHeader));
        }
      })
      .catch(() => { });
  }, []);

  const fetchJournals = () => {
    setLoading(true);
    setLoadError(null);
    const url = statusTab === 'all' ? '/api/accounting/journals' : `/api/accounting/journals?status=${statusTab}`;
    api.get(url)
      .then((res) => {
        const data = res.data;
        if (data.ok || data.success) {
          const list = Array.isArray(data.data) ? data.data : data.data?.items || [];
          setJournals(list);
        } else {
          // The accounting envelope is { ok:false, error:{ code, message } }.
          // Rendering `error` itself is what printed "[object Object]" on screen.
          setLoadError({ denied: false, message: data?.error?.message || 'The journal register could not be read.' });
        }
      })
      .catch((err) => {
        console.error(err);
        setLoadError({
          denied: err?.response?.status === 403,
          message: typeof err?.response?.data?.error?.message === 'string'
            ? err.response.data.error.message
            : 'The journal register did not respond.',
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchJournals();
  }, [statusTab]);

  const addLine = () => {
    setLineItems([...lineItems, { accountId: '', debit: '', credit: '', description: '' }]);
  };

  const updateLine = (index: number, field: keyof JournalLineInput, value: string) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };

  const removeLine = (index: number) => {
    if (lineItems.length <= 2) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const totalDebitMinor = lineItems.reduce((sum, item) => sum + Math.round((parseFloat(item.debit) || 0) * 100), 0);
  const totalCreditMinor = lineItems.reduce((sum, item) => sum + Math.round((parseFloat(item.credit) || 0) * 100), 0);
  const isBalanced = totalDebitMinor === totalCreditMinor && totalDebitMinor > 0;

  const handleCreateJournal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isBalanced) {
      alert('Total Debit must equal Total Credit and be greater than 0.');
      return;
    }

    try {
      const payload = {
        journalDate,
        narration,
        reference,
        lines: lineItems.map((item) => ({
          accountId: item.accountId,
          debit: Math.round((parseFloat(item.debit) || 0) * 100),
          credit: Math.round((parseFloat(item.credit) || 0) * 100),
          description: item.description || narration,
        })),
      };

      const res = await api.post('/api/accounting/journals', payload);
      const data = res.data;
      if (data.ok || data.success) {
        setShowCreateModal(false);
        setNarration('');
        setReference('');
        setLineItems([
          { accountId: '', debit: '', credit: '', description: '' },
          { accountId: '', debit: '', credit: '', description: '' },
        ]);
        setToastMessage('Draft journal voucher created successfully!');
        setTimeout(() => setToastMessage(null), 3000);
        fetchJournals();
      } else {
        alert(data.error?.message || 'Failed to create journal entry.');
      }
    } catch (err: any) {
      alert(err.response?.data?.error?.message || err.message);
    }
  };

  const handleAction = async (id: string, action: 'submit' | 'approve' | 'post' | 'reverse' | 'reject' | 'reopen') => {
    try {
      let body: any = {};
      if (action === 'reverse') {
        const reason = prompt('Please enter a reversal reason (min 20 characters):');
        if (!reason || reason.trim().length < 20) {
          alert('Reversal requires a detailed reason of at least 20 characters.');
          return;
        }
        body = { reason: reason.trim() };
      }
      if (action === 'reject') {
        const reason = prompt('Reason for rejecting this journal entry:');
        if (!reason || !reason.trim()) {
          alert('A rejection reason is required.');
          return;
        }
        body = { reason: reason.trim() };
      }

      const res = await api.post(`/api/accounting/journals/${id}/${action}`, body);
      const data = res.data;
      if (data.ok || data.success) {
        setToastMessage(`Journal entry ${action}ed successfully!`);
        setTimeout(() => setToastMessage(null), 3000);
        fetchJournals();
      } else {
        alert(data.error?.message || `Failed to ${action} journal entry.`);
      }
    } catch (err: any) {
      alert(err.response?.data?.error?.message || err.message);
    }
  };

  const handleDelete = async (j: any) => {
    if (!confirm(`Delete journal ${j.journalNo || '(draft)'}? This cannot be undone.`)) return;
    try {
      const res = await api.delete(`/api/accounting/journals/${j._id}`);
      const data = res.data;
      if (data.ok || data.success) {
        setToastMessage('Journal entry deleted.');
        setTimeout(() => setToastMessage(null), 3000);
        fetchJournals();
      } else {
        alert(data.error?.message || 'Failed to delete journal entry.');
      }
    } catch (err: any) {
      alert(err.response?.data?.error?.message || err.message);
    }
  };

  return (
    <div className="">
      <AccountingSubNav />

      <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
        {toastMessage && (
          <div role="status" className="flex items-center gap-2 rounded-card-sm border border-success/25 bg-success/10 p-4 text-sm text-success-text">
            <CheckCircle2 className="h-5 w-5 shrink-0" />
            {toastMessage}
          </div>
        )}

        <PageHeader icon={ScrollText} eyebrow="Finance" title="Manual Journal Entries" description="Create, review, approve and post double-entry manual journal vouchers." actions={<><div className="flex items-center gap-2">
            <button
              onClick={fetchJournals}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-outline bg-card px-3 text-body-sm font-medium text-on-surface-variant hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>

            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-body-sm font-semibold text-on-primary shadow-xs hover:bg-primary-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Plus className="w-3.5 h-3.5" /> New Journal Entry
            </button>
          </div></>} />

        {/* Status Tabs */}
        <div className="flex items-center flex-wrap gap-y-2 rounded-card border border-border bg-card p-2 text-on-surface-variant shadow-xs">
          {['all', 'draft', 'submitted', 'approved', 'posted', 'reversed', 'rejected'].map((tab, idx) => {
            let IconComponent = Plus;
            if (tab === 'all') IconComponent = Layers;
            else if (tab === 'draft') IconComponent = FileEdit;
            else if (tab === 'submitted') IconComponent = Send;
            else if (tab === 'approved') IconComponent = CheckCircle2;
            else if (tab === 'posted') IconComponent = Bookmark;
            else if (tab === 'reversed') IconComponent = RotateCcw;
            else if (tab === 'rejected') IconComponent = XCircle;

            const isActive = statusTab === tab;

            return (
              <div key={tab} className="flex items-center">
                {idx > 0 && (
                  <span aria-hidden="true" className="mx-1 h-4 w-px shrink-0 bg-border" />
                )}
                <button
                  onClick={() => setStatusTab(tab)}
                  aria-pressed={isActive}
                  className={cn(
                    "inline-flex h-8 items-center gap-1.5 rounded-lg border-0 bg-transparent px-2.5 text-body-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isActive
                      ? 'bg-primary-container font-semibold text-on-primary-container'
                      : 'text-on-surface-variant hover:bg-muted hover:text-on-surface'
                  )}
                >
                  <IconComponent className={cn("h-3.5 w-3.5", isActive ? "text-primary" : "text-on-surface-subtle")} />
                  <span className="capitalize">{tab}</span>
                </button>
              </div>
            );
          })}
        </div>

        {/* Journals Table */}
        <div className="overflow-hidden rounded-card border border-border bg-card shadow-xs">
          {loading ? (
            <div className="space-y-2 p-4" aria-busy="true" aria-live="polite">
              {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-12 w-full animate-pulse rounded-card-sm bg-muted" />)}
            </div>
          ) : loadError ? (
            <div role="alert" className={`m-4 rounded-card-sm border p-4 text-sm ${loadError.denied ? 'border-warning/25 bg-warning/10 text-warning-text' : 'border-danger/20 bg-danger/10 text-danger-text'}`}>
              <p className="font-semibold">{loadError.denied ? 'Permission denied' : 'Could not load journal vouchers'}</p>
              <p className="mt-1 text-body-sm">{loadError.denied
                ? 'Your role cannot read the journal register. Ask an Accounting administrator for read access.'
                : loadError.message}</p>
              <button type="button" onClick={fetchJournals} className="mt-2 rounded-lg border border-outline px-3 py-1.5 text-label-sm font-semibold uppercase tracking-wider text-on-surface hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Retry</button>
            </div>
          ) : journals.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="text-data-lg font-semibold text-on-surface">No journal entries {statusTab === 'all' ? 'yet' : `with status “${statusTab}”`}</p>
              <p className="mt-1 text-body-sm text-on-surface-variant">
                {statusTab === 'all'
                  ? 'Post a manual journal to record an adjustment that has no source document.'
                  : 'Switch tab to see vouchers at the next stage of approval.'}
              </p>
            </div>
          ) : (
            <table className="w-full text-left text-body-sm text-on-surface-variant">
              <thead className="border-b border-border bg-muted text-label-caps uppercase text-on-surface-variant">
                <tr>
                  <th className="px-4 py-2.5">Journal No</th>
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5">Narration & Reference</th>
                  <th className="px-4 py-2.5 text-right">Debit Total</th>
                  <th className="px-4 py-2.5 text-right">Credit Total</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {journals.map((j) => (
                  <tr key={j._id} className="hover:bg-muted/50">
                    <td className="px-4 py-2.5 font-mono tabular-nums font-medium text-on-surface">
                      {j.journalNo || 'Draft (unnumbered)'}
                    </td>
                    <td className="px-4 py-2.5 font-mono tabular-nums text-xs text-on-surface-subtle">
                      {new Date(j.journalDate).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-4 py-2.5 font-medium text-on-surface">
                      {j.narration}
                      {j.reference && <span className="ml-2 text-xs font-mono tabular-nums text-on-surface-subtle">[{j.reference}]</span>}
                      {j.status === 'rejected' && j.rejectionReason && (
                        <div className="mt-1 text-xs text-danger-text">Rejected: {j.rejectionReason}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums font-medium text-on-surface">
                      {formatINR(j.totalDebit / 100)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums font-medium text-on-surface">
                      {formatINR(j.totalCredit / 100)}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill status={j.status} />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {j.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handleAction(j._id, 'submit')}
                              className="inline-flex items-center gap-1 rounded-lg border border-info/25 px-2 py-1 text-label-caps font-semibold uppercase text-info-text hover:bg-info/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <Send className="w-3 h-3" /> Submit
                            </button>
                            <button
                              onClick={() => handleDelete(j)}
                              className="inline-flex items-center gap-1 rounded-lg border border-outline px-2 py-1 text-label-caps font-semibold uppercase text-on-surface-variant hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          </>
                        )}
                        {j.status === 'submitted' && (
                          <>
                            <button
                              onClick={() => handleAction(j._id, 'approve')}
                              className="inline-flex items-center gap-1 rounded-lg border border-success/25 px-2 py-1 text-label-caps font-semibold uppercase text-success-text hover:bg-success/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <CheckSquare className="w-3 h-3" /> Approve
                            </button>
                            <button
                              onClick={() => handleAction(j._id, 'reject')}
                              className="inline-flex items-center gap-1 rounded-lg border border-destructive/25 px-2 py-1 text-label-caps font-semibold uppercase text-destructive hover:bg-danger/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <XCircle className="w-3 h-3" /> Reject
                            </button>
                          </>
                        )}
                        {j.status === 'approved' && (
                          <>
                            <button
                              onClick={() => handleAction(j._id, 'post')}
                              className="inline-flex items-center gap-1 rounded-lg bg-primary px-2 py-1 text-label-caps font-semibold uppercase text-on-primary hover:bg-primary-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <ArrowUpRight className="w-3 h-3" /> Post to GL
                            </button>
                            <button
                              onClick={() => handleAction(j._id, 'reject')}
                              className="inline-flex items-center gap-1 rounded-lg border border-destructive/25 px-2 py-1 text-label-caps font-semibold uppercase text-destructive hover:bg-danger/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <XCircle className="w-3 h-3" /> Reject
                            </button>
                          </>
                        )}
                        {j.status === 'posted' && (
                          <button
                            onClick={() => handleAction(j._id, 'reverse')}
                            className="inline-flex items-center gap-1 rounded-lg border border-outline px-2 py-1 text-label-caps font-semibold uppercase text-on-surface-variant hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <RotateCcw className="w-3 h-3" /> Reverse
                          </button>
                        )}
                        {j.status === 'rejected' && (
                          <>
                            <button
                              onClick={() => handleAction(j._id, 'reopen')}
                              className="inline-flex items-center gap-1 rounded-lg border border-primary/25 px-2 py-1 text-label-caps font-semibold uppercase text-primary hover:bg-primary-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <Undo2 className="w-3 h-3" /> Reopen
                            </button>
                            <button
                              onClick={() => handleDelete(j)}
                              className="inline-flex items-center gap-1 rounded-lg border border-outline px-2 py-1 text-label-caps font-semibold uppercase text-on-surface-variant hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* New Journal Entry Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 max-w-2xl w-full shadow-2xl space-y-4 my-8">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Create New Journal Entry</h3>

            <form onSubmit={handleCreateJournal} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Journal Date</label>
                  <input
                    type="date"
                    required
                    value={journalDate}
                    onChange={(e) => setJournalDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Reference Code</label>
                  <input
                    type="text"
                    placeholder="e.g. JV-2026-001"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  />
                </div>

                <div className="md:col-span-1">
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Status</label>
                  <div className="px-3 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs font-mono font-semibold text-slate-600 dark:text-slate-400">
                    Draft Entry
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Narration / Description</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Month-end accrued utility expenses adjustment"
                  value={narration}
                  onChange={(e) => setNarration(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                />
              </div>

              {/* Line Items Grid */}
              <div className="space-y-2 pt-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Journal Lines</span>
                  <button
                    type="button"
                    onClick={addLine}
                    className="rounded-lg text-body-sm font-semibold text-primary hover:bg-primary-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    + Add line
                  </button>
                </div>

                {/* Column header so the two money inputs read as a debit/credit ledger. */}
                <div aria-hidden="true" className="grid grid-cols-12 items-center gap-2 text-label-sm uppercase tracking-wider text-on-surface-variant">
                  <span className="col-span-4">Account</span>
                  <span className="col-span-3 text-right">Debit</span>
                  <span className="col-span-3 text-right">Credit</span>
                  <span className="col-span-2 text-right">Line</span>
                </div>

                <div className="space-y-2">
                  {lineItems.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4">
                        <select
                          required
                          aria-label={`Line ${idx + 1} account`}
                          value={line.accountId}
                          onChange={(e) => updateLine(idx, 'accountId', e.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="">Select Account</option>
                          {accounts.map((acc) => (
                            <option key={acc._id} value={acc._id}>
                              {acc.code} — {acc.name} ({acc.type})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-span-3">
                        <input
                          type="number"
                          step="0.01"
                          aria-label={`Line ${idx + 1} debit amount`}
                          placeholder="0.00"
                          value={line.debit}
                          onChange={(e) => updateLine(idx, 'debit', e.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-right text-xs font-mono tabular-nums text-success-text placeholder:text-on-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </div>

                      <div className="col-span-3">
                        <input
                          type="number"
                          step="0.01"
                          aria-label={`Line ${idx + 1} credit amount`}
                          placeholder="0.00"
                          value={line.credit}
                          onChange={(e) => updateLine(idx, 'credit', e.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-right text-xs font-mono tabular-nums text-info-text placeholder:text-on-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </div>

                      <div className="col-span-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeLine(idx)}
                          aria-label={`Remove line ${idx + 1}`}
                          className="rounded-lg px-1 py-1 text-body-sm font-medium text-destructive hover:bg-danger/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Balance Validation Summary — the entry screen's debit/credit proof, in Indian grouping. */}
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-card-sm border border-border bg-muted/50 p-3 text-xs">
                <div>
                  <span className="text-label-sm uppercase tracking-wider text-on-surface-variant">Total Dr </span>
                  <span className="font-mono tabular-nums font-semibold text-on-surface">{formatINR(totalDebitMinor / 100)}</span>
                </div>
                <div>
                  <span className="text-label-sm uppercase tracking-wider text-on-surface-variant">Total Cr </span>
                  <span className="font-mono tabular-nums font-semibold text-on-surface">{formatINR(totalCreditMinor / 100)}</span>
                </div>
                <div>
                  {isBalanced ? (
                    <span className="inline-flex items-center gap-1 font-semibold text-success-text">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Balanced
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 font-semibold text-warning-text">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Out of balance by {formatINR(Math.abs(totalDebitMinor - totalCreditMinor) / 100)}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-border pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="rounded-lg px-4 py-2 text-body-sm font-medium text-on-surface-variant hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isBalanced}
                  className="rounded-lg bg-primary px-4 py-2 text-body-sm font-semibold text-on-primary shadow-xs hover:bg-primary-dim disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Save journal draft
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default JournalListPage;
